import React, { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, Check, CheckCheck, Paperclip, FileText, Upload, BookOpen, Loader2, Hand } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function timeStr(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return ""; }
}

// WhatsApp-style delivery ticks: ✓ sent · ✓✓ delivered · ✓✓ blue read · red = failed.
function StatusTicks({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "read") return <CheckCheck className="h-3.5 w-3.5 text-sky-500 inline" data-testid="wa-tick-read" />;
  if (s === "delivered") return <CheckCheck className="h-3.5 w-3.5 text-slate-400 inline" data-testid="wa-tick-delivered" />;
  if (["undelivered", "failed"].includes(s)) return <span className="text-red-500 text-[10px] font-medium" data-testid="wa-tick-failed">✕ undelivered</span>;
  if (["sent", "queued", "sending", "accepted", "scheduled"].includes(s)) return <Check className="h-3.5 w-3.5 text-slate-400 inline" data-testid="wa-tick-sent" />;
  return null;
}

// A WhatsApp conversation thread. Provide either leadId or phoneNorm.
export const WhatsAppChat = ({ leadId, phoneNorm, phoneDisplay, heightClass = "h-[420px]", hasLead = true, contactName = "" }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [greeting, setGreeting] = useState(false);
  const [configured, setConfigured] = useState(true);
  const [resolvedPhone, setResolvedPhone] = useState(phoneNorm || "");
  const [showAttach, setShowAttach] = useState(false);
  const [uploading, setUploading] = useState(false);
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  const canQuote = !!leadId || hasLead;

  const fetchMessages = async () => {
    try {
      const params = leadId ? { lead_id: leadId } : { phone_norm: phoneNorm };
      const { data } = await api.get("/whatsapp/messages", { params });
      setMessages(data.messages || []);
      if (data.phone_norm) setResolvedPhone(data.phone_norm);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    api.get("/whatsapp/status").then(({ data }) => setConfigured(data.configured)).catch(() => {});
    fetchMessages();
    const t = setInterval(fetchMessages, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [leadId, phoneNorm]);

  // Keep the chat scrolled to the latest message WITHOUT scrolling the whole page.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const doSend = async ({ body = "", attachments = [] } = {}) => {
    setSending(true);
    try {
      const base = leadId ? { lead_id: leadId } : { to_phone: phoneDisplay || resolvedPhone };
      const payload = { ...base, body, attachments };
      const { data } = await api.post("/whatsapp/send", payload);
      if (data?.status === "error") {
        toast.error(data.reason || "Couldn't send WhatsApp");
        return false;
      }
      setText("");
      await fetchMessages();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send WhatsApp");
      return false;
    } finally {
      setSending(false);
    }
  };

  const send = () => { if (text.trim()) doSend({ body: text.trim() }); };

  const sendGreeting = async () => {
    setGreeting(true);
    try {
      const base = leadId ? { lead_id: leadId } : { to_phone: phoneDisplay || resolvedPhone, name: contactName };
      const { data } = await api.post("/whatsapp/send-greeting", base);
      if (data?.status === "error") { toast.error(data.reason || "Couldn't send greeting"); return; }
      toast.success("Greeting template sent");
      await fetchMessages();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send greeting");
    } finally {
      setGreeting(false);
    }
  };

  const sendAttachment = async (key) => {
    setShowAttach(false);
    const ok = await doSend({ body: text.trim(), attachments: [key] });
    if (ok) toast.success("Attachment sent");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setShowAttach(false);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/whatsapp/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ok = await doSend({ body: text.trim(), attachments: [data.token] });
      if (ok) toast.success("File sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!configured) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 flex items-center gap-2">
        <MessageCircle className="h-4 w-4" /> WhatsApp isn't connected yet. Ask an admin to set it up in Lead Capture.
      </div>
    );
  }

  const attachItems = [
    { key: "catalog", label: "Product Catalog", icon: BookOpen },
    { key: "profile", label: "Company Profile", icon: FileText },
    ...(canQuote ? [{ key: "quotation", label: "Latest Quotation", icon: FileText }] : []),
  ];

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="whatsapp-chat">
      <div ref={scrollRef} className={`${heightClass} overflow-y-auto p-3 space-y-2 bg-[#efeae2]`} style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6),rgba(255,255,255,0.6))" }}>
        {messages.length === 0 && (
          <p className="text-center text-xs text-slate-400 mt-6">No messages yet. Send the first WhatsApp below.</p>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
            <div
              data-testid={`wa-msg-${m.direction}`}
              className={`max-w-[75%] rounded-lg px-3 py-2 text-sm shadow-sm ${
                m.direction === "outbound" ? "bg-[#d9fdd3] text-slate-800" : "bg-white text-slate-800"
              }`}
            >
              {m.media && m.media.length > 0 && (
                <div className="mb-1 space-y-1">
                  {m.media.map((md, i) => (
                    <p key={i} className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-white/60 rounded px-2 py-1">
                      <FileText className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{typeof md === "string" && md.startsWith("http") ? "Attachment" : md}</span>
                    </p>
                  ))}
                </div>
              )}
              {m.body && <p className="whitespace-pre-wrap break-words">{m.body}</p>}
              <p className="text-[10px] text-slate-400 mt-1 text-right flex items-center justify-end gap-1">
                {timeStr(m.created_at)}
                {m.direction === "outbound" && <StatusTicks status={m.status} />}
              </p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <div className="relative flex items-center gap-2 border-t border-slate-200 p-2 bg-white">
        {/* Send approved greeting template (works even after the 24h window) */}
        <Button
          type="button" size="icon" variant="ghost"
          onClick={sendGreeting} disabled={greeting}
          data-testid="wa-greeting-btn"
          title="Send approved greeting template (works even after 24h)"
          className="rounded-full text-slate-500 hover:text-emerald-600 shrink-0"
        >
          {greeting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Hand className="h-5 w-5" />}
        </Button>
        {/* Attach */}
        <div className="relative">
          <Button
            type="button" size="icon" variant="ghost"
            onClick={() => setShowAttach((v) => !v)}
            disabled={uploading}
            data-testid="wa-attach-btn"
            className="rounded-full text-slate-500 hover:text-emerald-600 shrink-0"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
          </Button>
          {showAttach && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowAttach(false)} />
              <div className="absolute bottom-12 left-0 z-20 w-52 rounded-lg border border-slate-200 bg-white shadow-lg py-1" data-testid="wa-attach-menu">
                {attachItems.map(({ key, label, icon: Ic }) => (
                  <button
                    key={key} onClick={() => sendAttachment(key)}
                    data-testid={`wa-attach-${key}`}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    <Ic className="h-4 w-4 text-emerald-600" /> {label}
                  </button>
                ))}
                <button
                  onClick={() => fileRef.current?.click()}
                  data-testid="wa-attach-upload"
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 border-t border-slate-100"
                >
                  <Upload className="h-4 w-4 text-emerald-600" /> Upload a file…
                </button>
              </div>
            </>
          )}
          <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="wa-file-input" />
        </div>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a WhatsApp message…"
          data-testid="wa-input"
          className="flex-1 h-10 rounded-full border border-slate-300 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
        />
        <Button onClick={send} disabled={sending || !text.trim()} data-testid="wa-send-btn" size="icon" className="rounded-full bg-emerald-600 hover:bg-emerald-700 shrink-0">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default WhatsAppChat;
